class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.applyPopupColorScheme();
    window.matchMedia('(min-width: 990px)').addEventListener('change', () => this.applyPopupColorScheme());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('active')) {
        this.close();
        return;
      }
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  isDesktopPopup() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  applyPopupColorScheme() {
    const inner = this.querySelector('.drawer__inner');
    const header = document.querySelector('.header-wrapper');
    if (!inner || !header) return;

    const scheme = this.isDesktopPopup()
      ? header.dataset.desktopPopupScheme
      : header.dataset.mobileNavScheme;
    if (!scheme) return;

    [...inner.classList].forEach((className) => {
      if (className.startsWith('color-')) inner.classList.remove(className);
    });
    inner.classList.add(`color-${scheme}`);
  }

  positionDesktopPopup() {
    if (!this.isDesktopPopup()) return;
    const header = document.querySelector('.header-wrapper');
    const sectionHeader = document.querySelector('.section-header');
    if (!header) return;

    const rect = header.getBoundingClientRect();
    let top = rect.bottom + 6;

    // If the sticky header is still mid-reveal (or was measured while hidden),
    // rect.bottom can be ~0–2px — fall back to the revealed layout height.
    if (top < 40 || sectionHeader?.classList.contains('shopify-section-header-hidden')) {
      const paddingTop = sectionHeader ? parseFloat(getComputedStyle(sectionHeader).paddingTop) || 0 : 0;
      top = paddingTop + header.offsetHeight + 6;
    }

    this.style.setProperty('--cart-popup-top', `${Math.round(top)}px`);
    this.style.setProperty('--cart-popup-right', `${Math.round(window.innerWidth - rect.right)}px`);
  }

  closeHeaderMenus() {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (typeof menu.close === 'function') menu.close();
    });
  }

  onOutsidePointerDown = (event) => {
    if (!this.classList.contains('active') || this._isClosing) return;
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper?.contains(event.target) || this.contains(event.target)) return;
    this.close();
  };

  bindOutsideClose() {
    this.unbindOutsideClose();
    requestAnimationFrame(() => {
      if (!this.classList.contains('active') && !document.querySelector('.header-wrapper.cart-open')) return;
      document.addEventListener('pointerdown', this.onOutsidePointerDown);
    });
  }

  unbindOutsideClose() {
    document.removeEventListener('pointerdown', this.onOutsidePointerDown);
  }

  open(triggeredBy) {
    if (this.classList.contains('active') || this._isSwitching) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.closeHeaderMenus();

    const headerDrawer = document.querySelector('header-drawer');
    const stickyHeader = document.querySelector('sticky-header');
    const sectionHeader = document.querySelector('.section-header');
    const headerWrapper = document.querySelector('.header-wrapper');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const beginOpen = () => {
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      const wasHidden = Boolean(sectionHeader?.classList.contains('shopify-section-header-hidden'));

      if (stickyHeader) stickyHeader.preventHide = true;
      this.applyPopupColorScheme();

      const expandCart = () => {
        if (!this.isDesktopPopup()) {
          sectionHeader?.classList.add('cart-open', 'animate');
          headerWrapper?.classList.add('cart-open', 'is-expanded');
          document.body.classList.add('overflow-hidden');
        }
        this.positionDesktopPopup();
        this.classList.add('animate', 'active');
        this.bindOutsideClose();
      };

      // Wait for the sticky header to finish sliding into view before opening /
      // positioning the cart (desktop popup top is measured from the header).
      if (wasHidden && !reduceMotion && sectionHeader) {
        sectionHeader.classList.add('animate');
        void sectionHeader.offsetWidth;
        if (!this.isDesktopPopup()) {
          sectionHeader.classList.add('cart-open');
        } else {
          stickyHeader?.reveal?.();
        }
        setTimeout(() => {
          stickyHeader?.reveal?.();
          requestAnimationFrame(() => {
            requestAnimationFrame(expandCart);
          });
        }, 450);
      } else {
        stickyHeader?.reveal?.();
        setTimeout(expandCart);
      }

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true },
      );

      // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
      // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
      // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    };

    if (headerDrawer?.isPillMenuOpen && !this.isDesktopPopup()) {
      this._isSwitching = true;
      sectionHeader?.classList.add('is-panel-switching');
      headerWrapper?.classList.add('is-panel-switching');

      const clearSwitching = () => {
        sectionHeader?.classList.remove('is-panel-switching');
        headerWrapper?.classList.remove('is-panel-switching');
        this._isSwitching = false;
      };

      headerDrawer
        .closeMenuDrawer(new Event('cart-open'), headerDrawer.menuToggle)
        .then(() => new Promise((resolve) => setTimeout(resolve, reduceMotion ? 0 : 100)))
        .then(() => {
          clearSwitching();
          beginOpen();
        })
        .catch(clearSwitching);
      return;
    }

    if (headerDrawer?.isPillMenuOpen) {
      headerDrawer.closeMenuDrawer(new Event('cart-open'), headerDrawer.menuToggle, {
        immediate: true,
        keepExpanded: true,
      });
    }

    beginOpen();
  }

  close(options = {}) {
    return new Promise((resolve) => {
      const immediate = Boolean(options.immediate);
      const keepExpanded = Boolean(options.keepExpanded);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const stickyHeader = document.querySelector('sticky-header');
      const sectionHeader = document.querySelector('.section-header');
      const headerWrapper = document.querySelector('.header-wrapper');

      const isOpen =
        this.classList.contains('active') || Boolean(document.querySelector('.header-wrapper.cart-open'));

      if (!isOpen && !this._isClosing) {
        resolve();
        return;
      }

      if (this._isClosing) {
        (this._closeWaiters ??= []).push(resolve);
        return;
      }

      this.unbindOutsideClose();
      removeTrapFocus(this.activeElement);

      const finishClose = () => {
        this._isClosing = false;
        this.classList.remove('active', 'animate');
        sectionHeader?.classList.remove('cart-open', 'is-cart-closing');
        headerWrapper?.classList.remove('cart-open', 'is-cart-closing');
        document.body.classList.remove('overflow-hidden');
        if (!headerWrapper?.classList.contains('menu-open')) {
          stickyHeader?.releaseVisibilityLock?.();
        }
        resolve();
        const waiters = this._closeWaiters ?? [];
        this._closeWaiters = [];
        waiters.forEach((waiter) => waiter());
      };

      if (this.isDesktopPopup() || immediate || reduceMotion || !headerWrapper?.classList.contains('is-expanded')) {
        if (!keepExpanded && !headerWrapper?.classList.contains('menu-open')) {
          headerWrapper?.classList.remove('is-expanded');
        }
        finishClose();
        return;
      }

      this._isClosing = true;
      headerWrapper.classList.remove('is-expanded');
      sectionHeader?.classList.add('is-cart-closing');
      headerWrapper?.classList.add('is-cart-closing');

      // Drop cart chrome immediately (while icons are faded) so cart + menu
      // can fade back in in their resting positions with the logo.
      requestAnimationFrame(() => {
        sectionHeader?.classList.remove('cart-open');
        headerWrapper?.classList.remove('cart-open');
        requestAnimationFrame(() => {
          sectionHeader?.classList.remove('is-cart-closing');
          headerWrapper?.classList.remove('is-cart-closing');
        });
      });

      const expand = headerWrapper.querySelector('.header-expand');
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        expand?.removeEventListener('transitionend', onEnd);
        clearTimeout(fallback);
        finishClose();
      };
      const onEnd = (e) => {
        if (e.target !== expand) return;
        if (e.propertyName && e.propertyName !== 'grid-template-rows') return;
        settle();
      };
      const fallback = setTimeout(settle, 450);
      expand?.addEventListener('transitionend', onEnd);
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.applyPopupColorScheme();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
